import * as React from 'react';
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { CheckboxField } from 'src/shared/index';
import { TextButton } from 'src/shared/ui/elements';
import {
  CheckboxWithConsent,
  CheckboxWithHoverLabel,
  FranchiseSubscriptions,
} from 'src/account/shared/ui/account-form-fields';

const NotificationTypes = {
  CLASS_NOTIFICATION: 'classNotification',
  NEWS_ANNOUNCEMENT: 'newsOrAnnouncement',
  EMAIL_NEWS_ANNOUNCEMENT: 'emailNewsOrAnnouncement',
  EMAIL_CLASS_NOTIFICATION: 'emailClassNotification',
};
export const SubscriptionFormFields = () => {
  const { control, setValue } = useFormContext();

  const { fields, update } = useFieldArray({
    control,
    name: 'franchisesOptIn',
  });

  const franchisesOptIn = useWatch({
    name: 'franchisesOptIn',
    control,
  });

  const newsOrAnnouncement = useWatch({
    name: NotificationTypes.NEWS_ANNOUNCEMENT,
    control,
  });
  const emailNewsOrAnnouncement = useWatch({
    name: NotificationTypes.EMAIL_NEWS_ANNOUNCEMENT,
    control,
  });
  const classNotification = useWatch({
    name: NotificationTypes.CLASS_NOTIFICATION,
    control,
  });

  const emailClassNotification = useWatch({
    name: NotificationTypes.EMAIL_CLASS_NOTIFICATION,
    control,
  });

  // Calculate bulk states based on franchisesOptIn
  const bulkStates = React.useMemo(() => {
    if (!franchisesOptIn?.length) {
      return {
        classNotification: classNotification,
        emailClassNotification: emailClassNotification,
        newsOrAnnouncement: newsOrAnnouncement,
        emailNewsOrAnnouncement: emailNewsOrAnnouncement,
        smsChecked: newsOrAnnouncement && classNotification,
        emailChecked: emailNewsOrAnnouncement && emailClassNotification,
      };
    }

    return {
      classNotification: franchisesOptIn.every((f) => f.classNotification),
      newsOrAnnouncement: franchisesOptIn.every((f) => f.newsOrAnnouncement),
      emailNewsOrAnnouncement: franchisesOptIn.every(
        (f) => f.emailNewsOrAnnouncement,
      ),
      emailClassNotification: franchisesOptIn.every(
        (f) => f.emailClassNotification,
      ),
      smsChecked: franchisesOptIn.every(
        (f) => f.newsOrAnnouncement && f.classNotification,
      ),
      emailChecked: franchisesOptIn.every(
        (f) => f.emailNewsOrAnnouncement && f.emailClassNotification,
      ),
    };
  }, [
    classNotification,
    emailClassNotification,
    emailNewsOrAnnouncement,
    franchisesOptIn,
    newsOrAnnouncement,
  ]);

  const handleExpandAll = () => {
    const allExpanded = franchisesOptIn.every((f) => f.isExpanded);
    franchisesOptIn.forEach((franchise, index) => {
      update(index, { ...franchise, isExpanded: !allExpanded });
    });
  };

  const setValues = (items, value) => {
    items.forEach((item) => {
      setValue(item, value, { shouldValidate: true });
    });
  };

  const handleAllOptIn = (e) => {
    const isChecked = e.target.checked;
    if (fields.length > 0) {
      fields.forEach((_, index) => {
        setValues(
          [
            `franchisesOptIn.${index}.${NotificationTypes.CLASS_NOTIFICATION}`,
            `franchisesOptIn.${index}.${NotificationTypes.NEWS_ANNOUNCEMENT}`,
            `franchisesOptIn.${index}.${NotificationTypes.EMAIL_NEWS_ANNOUNCEMENT}`,
          ],
          isChecked,
        );
      });
      return;
    }
    setValues(
      [
        NotificationTypes.CLASS_NOTIFICATION,
        NotificationTypes.NEWS_ANNOUNCEMENT,
        NotificationTypes.EMAIL_NEWS_ANNOUNCEMENT,
      ],
      isChecked,
    );
  };

  const handleSmsBulkOptIn = (e) => {
    const isChecked = e.target.checked;
    if (fields.length > 0) {
      fields.forEach((_, index) => {
        setValues(
          [
            `franchisesOptIn.${index}.${NotificationTypes.CLASS_NOTIFICATION}`,
            `franchisesOptIn.${index}.${NotificationTypes.NEWS_ANNOUNCEMENT}`,
          ],
          isChecked,
        );
      });
      return;
    }
    setValues(
      [
        NotificationTypes.CLASS_NOTIFICATION,
        NotificationTypes.NEWS_ANNOUNCEMENT,
      ],
      isChecked,
    );
  };

  const handleEmailBulkOptIn = (e) => {
    const isChecked = e.target.checked;
    if (fields.length > 0) {
      fields.forEach((_, index) => {
        setValues(
          [
            `franchisesOptIn.${index}.${NotificationTypes.EMAIL_NEWS_ANNOUNCEMENT}`,
          ],
          isChecked,
        );
      });
      return;
    }
    setValues([NotificationTypes.EMAIL_NEWS_ANNOUNCEMENT], isChecked);
  };

  const handleOptIn = (type) => (e) => {
    const isChecked = e.target.checked;
    if (fields.length > 0) {
      fields.forEach((_, index) => {
        setValues([`franchisesOptIn.${index}.${type}`], isChecked);
      });
      return;
    }
    setValues([type], isChecked);
  };

  return (
    <div className="tw-space-y-4">
      <div className="tw-mt-6">
        {(franchisesOptIn.length > 1 || fields.length > 0) && (
          <>
            <CheckboxField
              className="tw-mb-2"
              $label="Subscribe to all Email and Text messages"
              checked={bulkStates.smsChecked && bulkStates.emailChecked}
              onChange={handleAllOptIn}
            />
            <div className="tw-ml-6">
              <CheckboxField
                className="tw-mb-2"
                $label="Email"
                checked={bulkStates.emailChecked}
                onChange={handleEmailBulkOptIn}
              />
              <div className="tw-ml-6">
                <Controller
                  name={NotificationTypes.EMAIL_CLASS_NOTIFICATION}
                  control={control}
                  render={({ field: { onChange } }) => (
                    <CheckboxWithHoverLabel
                      checked={bulkStates.emailClassNotification}
                      onChange={(e) => {
                        onChange(e);
                        handleOptIn(NotificationTypes.EMAIL_CLASS_NOTIFICATION)(
                          e,
                        );
                      }}
                      className="tw-mb-2"
                      disabled
                      label="Class Updates (e.g., Time change, Progress Reports, etc.)"
                    />
                  )}
                />

                <Controller
                  name={NotificationTypes.EMAIL_NEWS_ANNOUNCEMENT}
                  control={control}
                  render={({ field: { onChange } }) => (
                    <CheckboxWithHoverLabel
                      checked={bulkStates.emailNewsOrAnnouncement}
                      onChange={(e) => {
                        onChange(e);
                        handleOptIn(NotificationTypes.EMAIL_NEWS_ANNOUNCEMENT)(
                          e,
                        );
                      }}
                      className="tw-mb-3"
                      label="Promotional (e.g., Newsletters, Re-enrollment, Summer Camps, etc.)"
                    />
                  )}
                />
              </div>

              <CheckboxField
                className="tw-mb-2"
                $label="Text"
                checked={bulkStates.smsChecked}
                onChange={handleSmsBulkOptIn}
              />

              <div className="tw-ml-6">
                <Controller
                  name={NotificationTypes.CLASS_NOTIFICATION}
                  control={control}
                  render={({ field: { onChange } }) => (
                    <CheckboxWithHoverLabel
                      label="Class Updates (e.g., Time change, Progress Reports, etc.)"
                      checked={bulkStates.classNotification}
                      onChange={(e) => {
                        onChange(e);
                        handleOptIn(NotificationTypes.CLASS_NOTIFICATION)(e);
                      }}
                      className="tw-mb-2"
                    />
                  )}
                />
                <Controller
                  name={NotificationTypes.NEWS_ANNOUNCEMENT}
                  control={control}
                  render={({ field: { onChange } }) => (
                    <CheckboxWithConsent
                      checked={bulkStates.newsOrAnnouncement}
                      onChange={(e) => {
                        onChange(e);
                        handleOptIn(NotificationTypes.NEWS_ANNOUNCEMENT)(e);
                      }}
                      isTextConsent
                      className="tw-mb-3"
                      label="Promotional (e.g., Newsletters, Re-enrollment, Summer Camps, etc.)"
                    />
                  )}
                />
              </div>
            </div>
          </>
        )}

        {fields.length > 1 && (
          <div className="tw-mt-6">
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
              <div />
              <TextButton
                type="button"
                onClick={handleExpandAll}
                className="tw-no-underline"
              >
                {franchisesOptIn.every((f) => f.isExpanded)
                  ? `Collapse ${fields.length > 1 ? 'All' : ''}`
                  : `Expand ${fields.length > 1 ? 'All' : ''}`}
              </TextButton>
            </div>
            {fields.map((field, index) => (
              <FranchiseSubscriptions key={field.id} index={index} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
